import React, { useMemo, useState } from "react";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  ColumnDef,
  FilterFn,
  getFilteredRowModel,
} from "@tanstack/react-table";
import {
  Stack,
  Tooltip,
  IconButton,
  AvatarGroup,
  Avatar,
  Chip,
  CircularProgress,
  Box,
  Typography,
} from "@mui/material";
import {
  DocumentTextIcon,
  CheckCircleIcon,
  XCircleIcon,
} from "@heroicons/react/24/solid";
import moment from "moment";
import { BonusProps, BuilderInfo, RoleType } from "../../providers/types";
import BuilderSplitModal from "../../components/BuilderSplitModalReview";
import PayrollModal from "../../modals/PayrollModal";
import ReviewerViewModal from "../../modals/ReviewerViewModal";
import NoRows from "../DataGrid/animations/NoRows";
import httpClient from "../../utils/httpClient";
import { compareBonusesForReviewPage } from "../../utils/reviewPageStatusOrder";
import { ExpandMore, ExpandLess } from "@mui/icons-material";
import { useData } from "../../providers/DataProvider";
import { lightBlue } from "@mui/material/colors";
import { CheckBadgeIcon, MinusCircleIcon } from "@heroicons/react/24/outline";

export type Decision = { isApproved: boolean | null; rejectionReason: string };

interface ReviewApprovalsTableProps {
  data: BonusProps[];
  role?: string | null;
  loading: boolean;
  searchTerm?: string;
  selectedStatusType?: number | string | null;
  selectedCommunities?: string[];
  selectedRegion?: { regionName: string } | null;
  /** Decision state owned by the parent page, keyed by jobId. */
  decisions: Record<number, Decision>;
  onApprove: (jobId: number, isApproved: boolean | null) => void;
  onReason: (jobId: number, reason: string) => void;
}

export const ReviewApprovalsTable: React.FC<ReviewApprovalsTableProps> = ({
  data,
  role,
  loading,
  searchTerm = "",
  selectedStatusType,
  selectedCommunities = [],
  selectedRegion,
  decisions,
  onApprove,
  onReason,
}) => {
  const { bonusType } = useData();
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  // ---- filtering / sorting (mirrors ReviewTable) --------------------------
  const filteredData = useMemo(() => {
    return data.filter((bonus) => {
      if (selectedStatusType && selectedStatusType !== "All") {
        if (bonus.statusTypeId !== selectedStatusType) return false;
      }
      if (selectedCommunities.length > 0) {
        if (!selectedCommunities.includes(bonus.subdivision)) return false;
      }
      if (selectedRegion && selectedRegion.regionName !== "All") {
        if (bonus.regionName !== selectedRegion.regionName) return false;
      }
      return true;
    });
  }, [data, selectedStatusType, selectedCommunities, selectedRegion]);

  const sortedFilteredData = useMemo(
    () => [...filteredData].sort(compareBonusesForReviewPage),
    [filteredData]
  );

  // ---- decision access (state lives in parent) ----------------------------
  const getDecision = (row: BonusProps): Decision =>
    decisions[row.jobId] ?? {
      isApproved: row.isApproved ?? null,
      rejectionReason: row.rejectionReason ?? "",
    };

  const isValidRejection = (d: Decision) =>
    d.isApproved === false && d.rejectionReason.trim() !== "";
  const isPendingRejection = (d: Decision) =>
    d.isApproved === false && d.rejectionReason.trim() === "";

  // ---- helpers (mirrors ReviewTable) --------------------------------------
  function stringAvatar(name: string) {
    if (!name) return { sx: { width: 24, height: 24 }, children: "?" };
    const parts = name.split(" ");
    return {
      sx: { width: 24, height: 24 },
      children: `${parts[0]?.[0] || ""}${parts[1]?.[0] || ""}`,
    };
  }

  const fmtMoney = (n?: number | null) =>
    n == null
      ? "—"
      : n.toLocaleString("en-US", { style: "currency", currency: "USD" });

  const fmtDate = (d?: string | null) =>
    !d || moment(d).isSame(moment("0001-01-01"))
      ? "N/A"
      : moment(d).format("M/D/YYYY");

  const downloadFile = async (url: string, filename: string) => {
    try {
      const response = await httpClient.get(url, { responseType: "blob" });
      const blob = new Blob([response.data], { type: response.data.type });
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = filename;
      link.click();
      window.URL.revokeObjectURL(link.href);
    } catch (error) {
      console.error("File download error:", error);
    }
  };

  const downloadSelectionSheet = async (jobId: number, bonus: any) => {
    const address = bonus.jobAddress.replace(/\s+/g, "");
    await downloadFile(
      `/api/cba/SelectionSheetsPdf/${jobId}`,
      `${bonus?.projectNumber || ""}${bonus?.projectNumber ? "-" : ""}${bonus?.jobNumber || ""
      }${bonus?.jobNumber ? "-" : ""}${address}-SelectionSheet.pdf`
    );
  };

  const downloadBuilderFolder = async (jobId: number, bonus: any) => {
    const address = bonus.jobAddress.replace(/\s+/g, "");
    await downloadFile(
      `/api/cba/mergedpdf/${jobId}`,
      `${bonus?.projectNumber || ""}${bonus?.projectNumber ? "-" : ""}${bonus?.jobNumber || ""
      }${bonus?.jobNumber ? "-" : ""}${address}-ConstructionFolder.pdf`
    );
  };

  const formatStatus = (status: number, bonus?: BonusProps): string => {
    const isDefaultDate = (date: string | Date) =>
      moment(date).isSame(moment("0001-01-01"));
    switch (status) {
      case 0:
        return "Not Started";
      case 1:
        return "In Upload Process";
      case 2:
        return "PM Review";
      case 3:
        return "SPM Review";
      case 4:
        return "DM Review";
      case 5:
        return "Ops Review";
      case 6:
        return bonusType === "ClosingBonus" ? "Ops Approved" : "Approved";
      case 7:
        return "Rejected";
      case 8:
        return "Busted";
      case 9:
        return bonus?.payrollProcessedOn && !isDefaultDate(bonus.payrollProcessedOn)
          ? `In Payroll Process (${moment(bonus.payrollProcessedOn).format(
            "MM/DD/YYYY"
          )})`
          : "In Payroll Process";
      case 10:
        return bonus?.payrollPaidOn && !isDefaultDate(bonus.payrollPaidOn)
          ? `Paid (${moment(bonus.payrollPaidOn).format("MM/DD/YYYY")})`
          : "Paid";
      default:
        return "Unknown Status";
    }
  };

  const rejectedStatusClass = "text-red-400 font-medium";

  const globalFilter: FilterFn<BonusProps> = (row, _columnId, filterValue) => {
    const searchValue = filterValue.toString().toLowerCase();
    const values = [
      row.original.jobAddress,
      row.original.subdivision,
      row.original.regionName,
      row.original.payoutInfo?.projectNumber,
      row.original.jobNumber,
      formatStatus(row.original.statusTypeId, row.original),
    ];
    return values.some(
      (value) => value && value.toString().toLowerCase().includes(searchValue)
    );
  };

  // ---- columns ------------------------------------------------------------
  const columns = useMemo<ColumnDef<BonusProps>[]>(
    () => [
      {
        id: "expand",
        size: 20,
        header: () => null,
        cell: ({ row }) => (
          <div className="flex justify-center">
            {expandedRows.has(row.original.jobId) ? (
              <ExpandLess className="text-gray-400" />
            ) : (
              <ExpandMore className="text-gray-400" />
            )}
          </div>
        ),
      },
      {
        id: "decision",
        size: 30,
        header: () => null,
        cell: ({ row }) => {
          if (row.original.statusTypeId !== 5) return null;

          const d = getDecision(row.original);
          if (d.isApproved === true)
            return (
              <Tooltip title="Approved">
                <CheckCircleIcon className="h-5 w-5 text-green-600" />
              </Tooltip>
            );
          if (isValidRejection(d))
            return (
              <Tooltip title={`Rejected: ${d.rejectionReason}`}>
                <XCircleIcon className="h-5 w-5 text-red-500" />
              </Tooltip>
            );
          return (
            <Tooltip title="Pending decision">
              <MinusCircleIcon className="h-5 w-5 text-gray-300" />
            </Tooltip>
          );
        },
      },
      {
        accessorKey: "jobAddress",
        header: "Address",
        size: 220,
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            {row.original.statusTypeId == 5 && row.original.isAutoApprovalEligible && (
              <span
                className="
            inline-flex items-center gap-1
            rounded-full bg-blue-50
            px-2 py-0.5
            text-xs font-medium text-blue-700
            ring-1 ring-inset ring-blue-200
            flex-shrink-0
          "
              >
                <CheckBadgeIcon className="h-3.5 w-3.5" />
                Auto-Eligible
              </span>
            )}

            <Tooltip title={row.original.jobAddress}>
              <span className="truncate block max-w-[260px]">
                {row.original.jobAddress}
              </span>
            </Tooltip>
          </div>
        ),
      },
      {
        accessorKey: "subdivision",
        header: "Community",
        size: 170,
      },
      {
        accessorKey: "projectNumber",
        header: "Job",
        size: 100,
        cell: ({ row }) => {
          const projectNumber = row.original?.projectNumber || "";
          const jobNumber = row.original?.jobNumber || "";
          return projectNumber && jobNumber
            ? `${projectNumber} - ${jobNumber}`
            : projectNumber || jobNumber;
        },
      },
      {
        accessorKey: bonusType === "Stage5Bonus" ? "stage5Date" : "closeDate",
        header: bonusType === "Stage5Bonus" ? "Stage 5 Date" : "Close Date",
        size: 120,
        cell: ({ row }) => {
          const raw =
            bonusType === "Stage5Bonus"
              ? row.original.stage5Date
              : row.original.closeDate;
          return !raw || moment(raw).isSame(moment("0001-01-01"))
            ? ""
            : moment(raw).format("MM/DD/YYYY");
        },
      },
      {
        accessorKey: "statusTypeId",
        header: "Status",
        size: 240,
        cell: ({ row }) => {
          const isRejected = row.original.statusTypeId === 7;
          const statusText = formatStatus(
            row.original.statusTypeId,
            row.original
          );
          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {row.original.isResubmittedJob ? (
                <Tooltip
                  title={`Job resubmitted after initial submission was rejected${row.original.isPayrollCycleSkipped
                    ? " | This job was skipped in the payroll cycle."
                    : ""
                    }`}
                >
                  <span
                    className={isRejected ? rejectedStatusClass : ""}
                    style={isRejected ? undefined : { color: "#a7352b" }}
                  >
                    {statusText} (Resubmitted)
                    {(row.original.isPayrollCycleSkipped && row.original.statusTypeId === 6) ? " ***" : ""}
                  </span>
                </Tooltip>
              ) : (row.original.isPayrollCycleSkipped && row.original.statusTypeId === 6) ? (
                <Tooltip title="This job was skipped in the payroll cycle.">
                  <span className={isRejected ? rejectedStatusClass : ""}>
                    {statusText} ***
                  </span>
                </Tooltip>
              ) : (
                <span className={isRejected ? rejectedStatusClass : ""}>
                  {statusText}
                </span>
              )}
            </Box>
          );
        },
      },
      {
        accessorKey: "builders",
        header: "Builders",
        size: 80,
        cell: ({ row }) => (
          <AvatarGroup
            max={2}
            sx={{
              "& .MuiAvatar-root": {
                width: 28,
                height: 28,
                fontSize: 15,
                bgcolor: "#003057",
              },
            }}
          >
            {row.original?.builders?.map((builder: BuilderInfo) => (
              <Tooltip key={builder.builderName} title={builder.builderName}>
                <Avatar {...stringAvatar(builder.builderName)} />
              </Tooltip>
            ))}
          </AvatarGroup>
        ),
      },
      {
        id: "actions",
        header: () => <div className="text-right">Actions</div>,
        size: 160,
        cell: ({ row }) => (
          <Stack
            direction="row"
            spacing={0.5}
            justifyContent="flex-end"
            alignItems="center"
            onClick={(e) => e.stopPropagation()}
            sx={{
              width: "100%",
            }}
          >
            {(row.original?.statusTypeId === 6 ||
              row.original?.statusTypeId === 9 ||
              row.original?.statusTypeId === 10) &&
              bonusType === "ClosingBonus" && (
                <>
                  <Tooltip title="Download Construction Folder">
                    <IconButton
                      onClick={() =>
                        downloadBuilderFolder(row.original.jobId, row.original)
                      }
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        className="h-5 w-5 text-primary-600"
                      >
                        <path
                          fillRule="evenodd"
                          d="M19.5 21a3 3 0 0 0 3-3V9a3 3 0 0 0-3-3h-5.379a.75.75 0 0 1-.53-.22L11.47 3.66A2.25 2.25 0 0 0 9.879 3H4.5a3 3 0 0 0-3 3v12a3 3 0 0 0 3 3h15Zm-6.75-10.5a.75.75 0 0 0-1.5 0v4.19l-1.72-1.72a.75.75 0 0 0-1.06 1.06l3 3a.75.75 0 0 0 1.06 0l3-3a.75.75 0 1 0-1.06-1.06l-1.72 1.72V10.5Z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Download Selection Sheet">
                    <IconButton
                      onClick={() =>
                        downloadSelectionSheet(row.original.jobId, row.original)
                      }
                    >
                      <DocumentTextIcon className="h-5 w-5 text-primary-600" />
                    </IconButton>
                  </Tooltip>
                </>
              )}
            <BuilderSplitModal jobId={row.original.jobId} />
            {role?.trim() === "Payroll" && bonusType === "ClosingBonus" && (
              <PayrollModal jobId={row.original.jobId as number} />
            )}
            {role?.trim() !== "Payroll" && role?.trim() !== "Uploader" && (
              <ReviewerViewModal jobId={row.original.jobId as number} />
            )}
          </Stack>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [role, expandedRows, bonusType, decisions]
  );

  const table = useReactTable({
    data: sortedFilteredData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    globalFilterFn: globalFilter,
    state: { globalFilter: searchTerm },
    filterFns: { global: globalFilter },
  });

  const toggleRowExpansion = (jobId: number) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(jobId)) next.delete(jobId);
      else next.add(jobId);
      return next;
    });
  };

  // ---- approval controls (top of detail panel) ----------------------------
  const renderApprovalControls = (row: BonusProps) => {
    const d = getDecision(row);
    const rejected = d.isApproved === false;
    const pending = isPendingRejection(d);
    return (
      <div className="flex items-center gap-6 bg-gray-100 rounded-md px-4 py-2 mb-4">
        <label className="flex items-center gap-1.5 cursor-pointer text-sm">
          <input
            type="radio"
            name={`decision-${row.jobId}`}
            checked={d.isApproved === undefined || d.isApproved === null}
            onChange={() => onApprove(row.jobId, null)}
          />
          <span>Unselect</span>
        </label>
        <label className="flex items-center gap-1.5 cursor-pointer text-sm">
          <input
            type="radio"
            name={`decision-${row.jobId}`}
            checked={d.isApproved === true}
            onChange={() => onApprove(row.jobId, true)}
          />
          <span>Approve</span>
        </label>
        <label className="flex items-center gap-1.5 cursor-pointer text-sm">
          <input
            type="radio"
            name={`decision-${row.jobId}`}
            checked={d.isApproved === false}
            onChange={() => onApprove(row.jobId, false)}
          />
          <span>Reject</span>
        </label>
        <input
          type="text"
          placeholder="Reason for rejection"
          disabled={!rejected}
          value={d.rejectionReason}
          onChange={(e) => onReason(row.jobId, e.target.value)}
          className={`flex-1 max-w-md h-9 px-3 text-sm rounded-md border focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:bg-gray-50 disabled:text-gray-400 ${pending ? "border-red-400" : "border-gray-300"
            }`}
        />
        {pending && (
          <span className="text-xs text-red-500">Reason required</span>
        )}
      </div>
    );
  };

  // ---- detail panel -------------------------------------------------------
  const renderDetailPanel = (row: BonusProps) => {
    const isDefaultDate = (date: string) =>
      moment(date).isSame(moment("0001-01-01"));

    return (
      <div className="px-4 py-3 bg-gray-50 border-t border-b border-gray-200">
        {row.statusTypeId === 5 && renderApprovalControls(row)}

        {/* Builder splits + Stage5 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
          <div className="space-y-3">
            {row.builders?.map((b) => (
              <div key={b.builderId} className="bg-white p-3 rounded-lg shadow-sm">
                <div className="font-medium text-gray-900">{b.builderName}</div>
                <div className="flex flex-wrap gap-2 my-1">
                  <Chip
                    label={`Split: ${b.split}%`}
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{ fontSize: { xs: "0.7rem", sm: "0.8125rem" } }}
                  />                  {b.splitAmount > 0 && (
                    <Chip
                      label={`Split Amount: $${b.splitAmount.toLocaleString()}`}
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ fontSize: { xs: "0.7rem", sm: "0.8125rem" } }}
                    />
                  )}
                  {b.deductionAmount > 0 && (
                    <Chip
                      label={`Deduction: $${b.deductionAmount.toLocaleString()}`}
                      size="small"
                      color="error"
                      variant="outlined"
                      sx={{ fontSize: { xs: "0.7rem", sm: "0.8125rem" } }}
                    />
                  )}
                  {b.splitAmount > 0 && (
                    <Chip
                      label={`${bonusType === "Stage5Bonus"
                        ? "Final Stage5 Amount: "
                        : "Final Closing Amount: "
                        }$${(b.splitAmount - b.deductionAmount).toLocaleString()}`}
                      size="small"
                      sx={{
                        bgcolor: lightBlue[100],
                        fontSize: { xs: "0.7rem", sm: "0.8125rem" }
                      }}
                      color="primary"
                      variant="outlined"
                    />
                  )}

                </div>
                {b.builderEmail && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mt: { xs: 0.75, sm: 1 }, fontSize: { xs: "0.7rem", sm: "0.75rem" } }}
                  >
                    {b.builderEmail}
                  </Typography>
                )}



              </div>
            ))}
          </div>

          <div className="bg-white p-3 rounded-lg shadow-sm">
            <h3 className="font-semibold text-sm mb-2 text-gray-900">Stage5</h3>
            <div className="space-y-1 text-sm">
              <div className="grid grid-cols-[180px_1fr] gap-x-6">
                <span className="text-gray-500">Stage5 Bonus Amount</span>
                <span className="font-semibold">{fmtMoney(row.stage5Bonus)}</span>
              </div>
              <div className="grid grid-cols-[180px_1fr] gap-x-6">
                <span className="text-gray-500">Stage5 Price</span>
                <span>{fmtMoney(row.stage5Price)}</span>
              </div>
              <div className="grid grid-cols-[180px_1fr] gap-x-6">
                <span className="text-gray-500">Stage5 Date</span>
                <span>{fmtDate(row.stage5Date)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Review cards (mirrors ReviewTable) */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {bonusType === "ClosingBonus" && (
            <div className="bg-white p-3 rounded-lg shadow-sm">
              <h3 className="font-semibold text-sm mb-2 text-gray-900">PM Review</h3>
              <div className="space-y-1 text-sm">
                <div>
                  <span className="text-gray-500">Reviewer:</span>{" "}
                  {row.pmReviewer ? row.pmReviewer.split("@")[0] : "N/A"}
                </div>
                <div>
                  <span className="text-gray-500">Review Date:</span>{" "}
                  {!isDefaultDate(row.pmReviewedOn)
                    ? moment(row.pmReviewedOn).format("MM/DD/YYYY h:mm A")
                    : "N/A"}
                </div>
              </div>
            </div>
          )}

          <div className="bg-white p-3 rounded-lg shadow-sm">
            <h3 className="font-semibold text-sm mb-2 text-gray-900">SPM Review</h3>
            <div className="space-y-1 text-sm">
              <div>
                <span className="text-gray-500">Reviewer:</span>{" "}
                {row.spmReviewer ? row.spmReviewer.split("@")[0] : "N/A"}
              </div>
              <div>
                <span className="text-gray-500">Review Date:</span>{" "}
                {!isDefaultDate(row.spmReviewedOn)
                  ? moment(row.spmReviewedOn).format("MM/DD/YYYY h:mm A")
                  : "N/A"}
              </div>
            </div>
          </div>

          <div className="bg-white p-3 rounded-lg shadow-sm">
            <h3 className="font-semibold text-sm mb-2 text-gray-900">DM Review</h3>
            <div className="space-y-1 text-sm">
              <div>
                <span className="text-gray-500">Reviewer:</span>{" "}
                {row.dmReviewer ? row.dmReviewer.split("@")[0] : "N/A"}
              </div>
              <div>
                <span className="text-gray-500">Review Date:</span>{" "}
                {!isDefaultDate(row.dmReviewedOn)
                  ? moment(row.dmReviewedOn).format("MM/DD/YYYY h:mm A")
                  : "N/A"}
              </div>
            </div>
          </div>

          <div className="bg-white p-3 rounded-lg shadow-sm">
            <h3 className="font-semibold text-sm mb-2 text-gray-900">Ops Review</h3>
            <div className="space-y-1 text-sm">
              <div>
                <span className="text-gray-500">Reviewer:</span>{" "}
                {row.opsReviewer ? row.opsReviewer.split("@")[0] : "N/A"}
              </div>
              <div>
                <span className="text-gray-500">Review Date:</span>{" "}
                {!isDefaultDate(row.opsReviewedOn)
                  ? moment(row.opsReviewedOn).format("MM/DD/YYYY h:mm A")
                  : "N/A"}
              </div>
            </div>
          </div>

          <div className="bg-white p-3 rounded-lg shadow-sm">
            <h3 className="font-semibold text-sm mb-2 text-gray-900">Payroll</h3>
            <div className="space-y-1 text-sm">
              <div>
                <span className="text-gray-500">Processed Date:</span>{" "}
                {!isDefaultDate(row.payrollProcessedOn)
                  ? moment(row.payrollProcessedOn).format("MM/DD/YYYY h:mm A")
                  : "N/A"}
              </div>
              <div>
                <span className="text-gray-500">Paid Date:</span>{" "}
                {!isDefaultDate(row.payrollPaidOn)
                  ? moment(row.payrollPaidOn).format("MM/DD/YYYY")
                  : "N/A"}
              </div>
            </div>
          </div>

          {bonusType === "ClosingBonus" && (
            <div className="bg-white p-3 rounded-lg shadow-sm">
              <h3 className="font-semibold text-sm mb-2 text-gray-900">Uploaded By</h3>
              <div className="space-y-1 text-sm">
                <div>
                  <span className="text-gray-500">Updated By:</span>{" "}
                  {row.updatedBy ? row.updatedBy.split("@")[0] : "N/A"}
                </div>
                <div>
                  <span className="text-gray-500">Updated On:</span>{" "}
                  {!isDefaultDate(row.updatedOn)
                    ? moment(row.updatedOn).format("MM/DD/YYYY h:mm A")
                    : "N/A"}
                </div>
              </div>
            </div>
          )}

          {/* Comments */}
          <div className="col-span-full bg-white p-3 rounded-lg shadow-sm">
            <h3 className="font-semibold text-sm mb-2 text-gray-900">
              Review Comments
            </h3>
            <div className="space-y-2">
              {row.comments && row.comments.length > 0 ? (
                row.comments.map((comment, index) => (
                  <div
                    key={comment.id || index}
                    className="border-l-2 border-gray-200 pl-3 text-sm"
                  >
                    <div >
                      <span className="font-medium text-gray-900">
                        {comment.createdBy.split("@")[0].replace(".", " ")}
                      </span>
                      <span className="ml-2 font-sm text-gray-600">
                        ({RoleType[comment?.roleTypeId] || "Unknown"})
                      </span>
                      <span className="ml-2 text-xs text-gray-400">
                        {moment(comment.createdOn).format("MM/DD/YYYY h:mm A")}
                      </span>
                    </div>
                    <Box my={1} sx={{ pl: 0.5 }} className="text-gray-600">
                      {(() => {
                        const raw = comment?.comment || "";
                        const parts = raw.replace(/\s*\|\s*/g, "|")
                          .split("|")
                          .map((s: string) => s.trim())
                          .filter(Boolean);

                        const KNOWN_LABELS = [
                          "Builder Split Update:",
                          "Bonus Deduction Explanation:",
                          "Unassigned Bonus Explanation:",
                          "Unassigned Bonus Reference:",
                          "Payroll Cycle Skipped:",
                          "Payroll Cycle Unskipped:",
                          "Payroll Exclusion Added:",
                          "Payroll Exclusion Removed:"
                        ];

                        const hasStructure = parts.some((p: string) =>
                          KNOWN_LABELS.some(label => p.startsWith(label))
                        );

                        if (!hasStructure) {
                          return (
                            <Typography variant="caption" color="CaptionText"
                              sx={{ fontSize: { xs: "0.75rem", sm: "0.8125rem" } }}>
                              {raw}
                            </Typography>
                          );
                        }

                        return (
                          <Stack spacing={0.75}>
                            {parts.map((part: string, i: number) => {
                              const matchedLabel = KNOWN_LABELS.find(label => part.startsWith(label));
                              if (matchedLabel) {
                                const labelText = matchedLabel.replace(/:$/, "");
                                const bodyText = part.slice(matchedLabel.length).trim();
                                const isHeader = matchedLabel === "Builder Split Update:";
                                return (
                                  <Box key={i}>
                                    <Typography variant="caption" sx={{
                                      fontWeight: 700,
                                      fontSize: { xs: "0.7rem", sm: "0.775rem" },
                                      color: isHeader ? "text.secondary" : "primary.700",
                                      textTransform: "uppercase",
                                      letterSpacing: 0.4,
                                      display: "block",
                                    }}>
                                      {labelText}
                                    </Typography>
                                    {bodyText && (
                                      <Typography variant="caption" color="CaptionText" sx={{
                                        fontSize: { xs: "0.75rem", sm: "0.8125rem" },
                                        display: "block",
                                        wordBreak: "break-word",
                                      }}>
                                        {bodyText}
                                      </Typography>
                                    )}
                                  </Box>
                                );
                              }
                              // Unlabeled segment
                              return (
                                <Typography key={i} variant="caption" color="CaptionText"
                                  sx={{ fontSize: { xs: "0.75rem", sm: "0.8125rem" }, wordBreak: "break-word" }}>
                                  {part}
                                </Typography>
                              );
                            })}
                          </Stack>
                        );
                      })()}
                    </Box>
                    {comment.fileAttachmentName && (
                      <div className="mt-1 text-blue-600">
                        Attachment: {comment.fileAttachmentName}
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <div className="text-sm text-gray-500 italic">
                  No comments available
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ---- states -------------------------------------------------------------
  if (loading) {
    return (
      <div className="flex justify-center items-center h-full min-h-[400px]">
        <CircularProgress />
      </div>
    );
  }

  if (!filteredData.length) {
    return (
      <div className="flex justify-center items-center h-full min-h-[400px]">
        <NoRows />
      </div>
    );
  }

  return (
    <div className="overflow-x-auto h-full">
      <table className="min-w-full divide-y divide-gray-200 table-fixed">
        <thead className="bg-gray-50 sticky top-0 z-10">
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${header.id === "actions"
                    ? "sticky right-0 bg-gray-50 z-20"
                    : ""
                    }`}
                  style={{
                    width: header.getSize(),
                    minWidth: header.getSize(),
                    ...(header.id === "actions"
                      ? {
                        backgroundImage:
                          "linear-gradient(to right, rgba(240,240,240,0.5) 0%, rgba(249,250,251,1) 15%)",
                      }
                      : {}),
                  }}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {table.getRowModel().rows.map((row) => (
            <React.Fragment key={row.original.jobId}>
              <tr
                className="hover:bg-gray-50 cursor-pointer"
                onClick={() => toggleRowExpansion(row.original.jobId)}
              >
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    className={`py-4 whitespace-nowrap text-sm text-gray-500 ${cell.column.id === "actions"
                        ? "sticky right-0 bg-white z-10 px-3"
                        : "px-6"
                      } ${expandedRows.has(row.original.jobId) &&
                        cell.column.id === "actions"
                        ? "bg-gray-50"
                        : ""
                      }`}
                    style={
                      cell.column.id === "actions"
                        ? {
                          backgroundImage:
                            "linear-gradient(to right, rgba(240,240,240,0.5) 0%, rgba(255,255,255,1) 15%)",
                        }
                        : {}
                    }
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
              {expandedRows.has(row.original.jobId) && (
                <tr>
                  <td colSpan={columns.length}>
                    {renderDetailPanel(row.original)}
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default React.memo(ReviewApprovalsTable);